import { apiInvoker } from '../../lib/apiInvoker';
import { API_URLS } from '../../lib/apiUrls';
import {
  fetchScriptsStart,
  fetchScriptsSuccess,
  fetchScriptsFailure,
  fetchScriptStart,
  fetchScriptSuccess,
  fetchScriptFailure,
  updateScriptShareStatus,
  deleteScriptStart,
  deleteScriptSuccess,
  deleteScriptFailure,
} from '../slices/scriptsSlice';
import { showSuccess } from '../../utils/toast';
import { handleApiError } from '../../utils/errorHandler';

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

export const fetchScripts = (forceRefresh = false) => {
  return async (dispatch, getState) => {
    const { scripts: scriptsState } = getState();

    // Check if we have cached data that's still valid
    if (!forceRefresh && scriptsState.lastFetched) {
      const timeSinceLastFetch = Date.now() - scriptsState.lastFetched;
      if (timeSinceLastFetch < CACHE_DURATION && scriptsState.scripts.length > 0) {
        // Use cached data, skip API call
        return;
      }
    }

    try {
      dispatch(fetchScriptsStart());
      const url = API_URLS.USER.GET_SCRIPTS;
      const response = await apiInvoker.get(url);
      
      if (response?.success && response?.data) {
        dispatch(fetchScriptsSuccess(response.data));
      } else if (response && !response.success) {
        throw new Error(response?.message || 'Failed to fetch scripts');
      } else if (response) {
        const scripts = Array.isArray(response) ? response : (response?.scripts || []);
        dispatch(fetchScriptsSuccess(scripts));
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Scripts fetch error:', error);
      
      const { message: errorMessage } = handleApiError(error, {
        defaultMessage: 'Failed to fetch scripts',
        skipToastForStatus: [401, 403],
      });
      
      dispatch(fetchScriptsFailure(errorMessage));
    }
  };
};

export const fetchScript = (scriptId) => {
  return async (dispatch) => {
    try {
      dispatch(fetchScriptStart());
      const url = API_URLS.USER.GET_SCRIPT(scriptId);
      const response = await apiInvoker.get(url);
      
      if (response?.success && response?.data) {
        dispatch(fetchScriptSuccess(response.data));
        return response.data;
      } else if (response && !response.success) {
        throw new Error(response?.message || 'Failed to fetch script');
      } else if (response) {
        dispatch(fetchScriptSuccess(response));
        return response;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Script fetch error:', error);
      
      const { message: errorMessage } = handleApiError(error, {
        defaultMessage: 'Failed to fetch script',
        customMessages: {
          404: 'Script not found',
        },
        skipToastForStatus: [401, 403],
      });
      
      dispatch(fetchScriptFailure(errorMessage));
      throw error;
    }
  };
};

export const exportScript = (scriptId) => {
  return async (dispatch) => {
    try {
      const url = API_URLS.USER.EXPORT_SCRIPT(scriptId);
      const response = await apiInvoker.post(url);
      
      if (response?.success && response?.data?.docxDownloadUrl) {
        const link = document.createElement('a');
        link.href = response.data.docxDownloadUrl;
        link.download = `script-${scriptId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showSuccess(response?.message || 'Script exported successfully');
      } else if (response && !response.success) {
        throw new Error(response?.message || 'Failed to export script');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Script export error:', error);
      
      handleApiError(error, {
        defaultMessage: 'Failed to export script',
      });
    }
  };
};

export const shareScript = (scriptId) => {
  return async (dispatch) => {
    try {
      const url = API_URLS.USER.SHARE_SCRIPT(scriptId);
      const response = await apiInvoker.post(url);
      
      if (response?.success) {
        showSuccess(response?.message || 'Script shared successfully');
        // Update isShared status locally without refetching to prevent data changes
        dispatch(updateScriptShareStatus({ scriptId, isShared: true }));
        return true;
      } else if (response && !response.success) {
        throw new Error(response?.message || 'Failed to share script');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Script share error:', error);
      
      handleApiError(error, {
        defaultMessage: 'Failed to share script',
      });
      
      return false;
    }
  };
};

export const unshareScript = (scriptId) => {
  return async (dispatch) => {
    try {
      const url = API_URLS.USER.UNSHARE_SCRIPT(scriptId);
      const response = await apiInvoker.post(url);
      
      if (response?.success) {
        showSuccess(response?.message || 'Script unshared successfully');
        // Update isShared status locally without refetching to prevent data changes
        dispatch(updateScriptShareStatus({ scriptId, isShared: false }));
        return true;
      } else if (response && !response.success) {
        throw new Error(response?.message || 'Failed to unshare script');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Script unshare error:', error);
      
      handleApiError(error, {
        defaultMessage: 'Failed to unshare script',
      });
      
      return false;
    }
  };
};

export const updateScript = (scriptId, content, status, imageFile) => {
  return async (dispatch) => {
    try {
      if (!scriptId) {
        throw new Error('Script ID is required');
      }
      
      const url = API_URLS.USER.UPDATE_SCRIPT(scriptId);
      
      let requestBody;
      if (imageFile) {
        const formData = new FormData();
        if (content) {
          formData.append('content', content);
        }
        if (status) {
          formData.append('status', status);
        }
        formData.append('image', imageFile);
        requestBody = formData;
      } else {
        requestBody = {};
        if (content) {
          requestBody.content = content;
        }
        if (status) {
          requestBody.status = status;
        }
      }
      
      const response = await apiInvoker.put(url, requestBody);
      
      if (response?.success && response?.data) {
        showSuccess(response?.message || 'Script updated successfully');
        dispatch(fetchScriptSuccess(response.data));
        dispatch(fetchScripts(true));
        return response.data;
      } else if (response && !response.success) {
        throw new Error(response?.message || 'Failed to update script');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      handleApiError(error, {
        defaultMessage: 'Failed to update script',
      });
      
      throw error;
    }
  };
};

export const deleteScript = (scriptId) => {
  return async (dispatch) => {
    try {
      dispatch(deleteScriptStart());
      const url = API_URLS.USER.DELETE_SCRIPT(scriptId);
      const response = await apiInvoker.delete(url);
      
      if (response?.success) {
        showSuccess(response?.message || 'Script deleted successfully');
        dispatch(deleteScriptSuccess(scriptId));
        return true;
      } else if (response && !response.success) {
        throw new Error(response?.message || 'Failed to delete script');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Script delete error:', error);
      
      dispatch(deleteScriptFailure());
      handleApiError(error, {
        defaultMessage: 'Failed to delete script',
      });
      
      return false;
    }
  };
};
